// =============================================================================
// ResiGen valuation tables + prepayment calculation — deliberately Meteor-free (no imports beyond
// types) so the math can be executed and validated standalone, mirroring the btaModel pattern.
// =============================================================================

// --- Valuation tables (stored on Org.resigen; absent → LUMINIA defaults below) ---

export interface ResigenValuationTier {
  minKw: number;      // exclusive lower bound (kW or kWh), except 0 which is inclusive
  maxKw: number;      // inclusive upper bound
  standard: number;   // $/W DC (solar) or $/kWh (battery)
  ecOrDc: number;     // Energy Community OR Disadvantaged Community
  ecPlusDc: number;   // both adders
}

export interface ResigenTables {
  solarTiers: ResigenValuationTier[];    // $/W DC by system-size tier
  batteryTiers: ResigenValuationTier[];  // $/kWh by capacity tier
  contribution: { standard: number, ecOrDc: number, ecPlusDc: number };  // tax-equity discount (fractions)
}

// Luminia default prepaid valuation tables (per Trevor, 2026-08-07 JIRA).
export const resigenDefaultTables: ResigenTables = {
  solarTiers: [
    { minKw: 0, maxKw: 7, standard: 5.75, ecOrDc: 6.00, ecPlusDc: 6.25 },
    { minKw: 7, maxKw: 15, standard: 5.75, ecOrDc: 6.00, ecPlusDc: 6.25 },
    { minKw: 15, maxKw: 50, standard: 5.25, ecOrDc: 5.75, ecPlusDc: 6.00 },
  ],
  batteryTiers: [
    { minKw: 0, maxKw: 15, standard: 2000, ecOrDc: 2200, ecPlusDc: 2400 },
    { minKw: 15, maxKw: 40.9, standard: 2000, ecOrDc: 2200, ecPlusDc: 2400 },
    { minKw: 40.9, maxKw: 45, standard: 1700, ecOrDc: 2000, ecPlusDc: 2200 },
    { minKw: 45, maxKw: 60, standard: 1500, ecOrDc: 1800, ecPlusDc: 2000 },
    { minKw: 60, maxKw: 100, standard: 1100, ecOrDc: 1300, ecPlusDc: 1500 },
  ],
  contribution: { standard: 0.185, ecOrDc: 0.20, ecPlusDc: 0.25 },
};

export type ResigenItcStatus = 'standard' | 'ecOrDc' | 'ecPlusDc';

/** The adder column to read: none → standard, one → ecOrDc, both → ecPlusDc. */
export const resigenItcStatus = (energyCommunity?: boolean, disadvantagedCommunity?: boolean): ResigenItcStatus =>
  energyCommunity && disadvantagedCommunity ? 'ecPlusDc' : (energyCommunity || disadvantagedCommunity ? 'ecOrDc' : 'standard');

export const resigenItcStatusLabels: { [k in ResigenItcStatus]: string } = {
  standard: 'Standard', ecOrDc: 'EC or DC', ecPlusDc: 'EC + DC',
};

/**
 * Finds the tier containing `size`, using min < size <= max (the table's "7.01–15" wording with
 * min 7.0 means exactly 7.0 kW belongs to the tier BELOW). Size 0 belongs to the first tier.
 * @param tiers - the org's tier rows
 * @param size - system size (kW) or capacity (kWh)
 * @returns the matching tier, or undefined when the size is outside every tier
 */
export const resigenTierFor = (tiers: ResigenValuationTier[], size: number): ResigenValuationTier | undefined =>
  tiers.find((t) => (size === 0 && t.minKw === 0) || (size > t.minKw && size <= t.maxKw));

export interface ResigenCalcResult {
  itcStatus: ResigenItcStatus;
  solarRate: number;          // $/W DC
  batteryRate: number;        // $/kWh
  contributionPct: number;    // fraction
  solarValue: number;         // kW × $/W × 1000
  batteryValue: number;       // kWh × $/kWh
  grossValue: number;
  contributionAmount: number;
  prepaymentAmount: number;
}

/**
 * The ResiGen calculation: gross project value from the valuation tables, less the tax-equity
 * contribution. Throws with a human-readable message when a size falls outside the tables.
 * @param tables - the org's tables (or the defaults)
 * @param input - the dealer's four inputs
 * @returns the full breakdown incl. the prepayment amount
 */
export const resigenCalculate = (
  tables: ResigenTables,
  input: { systemSizeKw: number, essCapacityKwh: number, energyCommunity?: boolean, disadvantagedCommunity?: boolean },
): ResigenCalcResult => {
  const { systemSizeKw, essCapacityKwh } = input;
  if (!(systemSizeKw >= 0) || !(essCapacityKwh >= 0)) throw new Error('Sizes must be zero or positive');
  if (systemSizeKw === 0 && essCapacityKwh === 0) throw new Error('Enter a system size or an ESS capacity');
  const itcStatus = resigenItcStatus(input.energyCommunity, input.disadvantagedCommunity);
  const solarTier = resigenTierFor(tables.solarTiers, systemSizeKw);
  if (!solarTier) throw new Error(`Solar system size ${systemSizeKw} kW is outside the valuation table`);
  const batteryTier = resigenTierFor(tables.batteryTiers, essCapacityKwh);
  if (!batteryTier) throw new Error(`ESS capacity ${essCapacityKwh} kWh is outside the valuation table`);
  const solarRate = solarTier[itcStatus];
  const batteryRate = batteryTier[itcStatus];
  const contributionPct = tables.contribution[itcStatus];
  const solarValue = systemSizeKw * solarRate * 1000;  // $/W DC × 1000 = $/kW DC
  const batteryValue = essCapacityKwh * batteryRate;
  const grossValue = solarValue + batteryValue;
  const contributionAmount = grossValue * contributionPct;
  return {
    itcStatus, solarRate, batteryRate, contributionPct,
    solarValue, batteryValue, grossValue, contributionAmount,
    prepaymentAmount: grossValue - contributionAmount,
  };
};

